import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { pickBlockingSession, sessionMessage } from '../utils/unsavedSession';

const DEFAULT_MESSAGE =
  'Hay una edición en curso. Guarda o descarta los cambios para continuar.';

const UnsavedSessionContext = createContext(null);

export function UnsavedSessionProvider({ children }) {
  const sessionsRef = useRef(new Map());
  const [dialog, setDialog] = useState(null);
  const [resolving, setResolving] = useState(false);

  const registerSession = useCallback((id, session) => {
    if (!id) return;
    if (!session) sessionsRef.current.delete(id);
    else sessionsRef.current.set(id, session);
  }, []);

  const requestLeave = useCallback((action) => {
    const next = typeof action === 'function' ? action : () => {};
    const blocking = pickBlockingSession([...sessionsRef.current.values()]);
    if (!blocking) {
      next();
      return true;
    }
    setDialog({ session: blocking, pendingAction: next });
    return false;
  }, []);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!pickBlockingSession([...sessionsRef.current.values()])) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const closeDialog = useCallback(() => setDialog(null), []);

  const settleAndLeave = useCallback(
    async (kind) => {
      const current = dialog;
      if (!current?.session) return;
      setResolving(true);
      try {
        if (kind === 'save') await current.session.save?.();
        if (kind === 'discard') await current.session.discard?.();
      } finally {
        setResolving(false);
      }
      sessionsRef.current.delete(current.session.id);
      setDialog(null);
      window.setTimeout(() => {
        const still = pickBlockingSession([...sessionsRef.current.values()]);
        if (still) {
          setDialog({ session: still, pendingAction: current.pendingAction });
          return;
        }
        current.pendingAction?.();
      }, 0);
    },
    [dialog]
  );

  const value = useMemo(
    () => ({ registerSession, requestLeave }),
    [registerSession, requestLeave]
  );

  return (
    <UnsavedSessionContext.Provider value={value}>
      {children}
      <Dialog
        open={Boolean(dialog)}
        onClose={resolving ? undefined : closeDialog}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 80 }}
      >
        <DialogTitle>Se está editando</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {sessionMessage(dialog?.session, DEFAULT_MESSAGE)}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button onClick={closeDialog} disabled={resolving}>
            Seguir editando
          </Button>
          <Button onClick={() => settleAndLeave('discard')} disabled={resolving} color="inherit">
            Descartar
          </Button>
          <Button onClick={() => settleAndLeave('save')} disabled={resolving} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </UnsavedSessionContext.Provider>
  );
}

export function useRequestLeave() {
  const ctx = useContext(UnsavedSessionContext);
  return ctx?.requestLeave || ((action) => {
    if (typeof action === 'function') action();
    return true;
  });
}

/**
 * Declara una sesión de edición. `spec` se lee en vivo (save/discard/dirty/message).
 */
export function useRegisterUnsavedSession(id, active, spec) {
  const ctx = useContext(UnsavedSessionContext);
  const specRef = useRef(spec);
  specRef.current = spec;

  useEffect(() => {
    if (!ctx || !id) return undefined;
    if (!active) {
      ctx.registerSession(id, null);
      return undefined;
    }
    ctx.registerSession(id, {
      id,
      dirty: () => Boolean(specRef.current?.dirty),
      message: () => specRef.current?.message,
      save: () => specRef.current?.save?.(),
      discard: () => specRef.current?.discard?.(),
    });
    return () => ctx.registerSession(id, null);
  }, [ctx, id, active]);
}
